import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Trash2, Download, Share2, Plus, Folder, FileText } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const mockSavedItems = [
  {
    id: 1,
    type: "article",
    title: "Tesla Q4 2024 Earnings Deep Dive",
    source: "Official PR",
    savedDate: "2 days ago",
    tags: ["Tesla", "Earnings"],
  },
  {
    id: 2,
    type: "thread",
    title: "SpaceX Starship Timeline Analysis",
    source: "Community",
    savedDate: "5 days ago",
    tags: ["SpaceX", "Starship"],
  },
  {
    id: 3,
    type: "chart",
    title: "Tesla Stock Performance 2025",
    source: "Markets",
    savedDate: "1 week ago",
    tags: ["Tesla", "Markets"],
  },
];

const mockCollections = [
  {
    id: 1,
    name: "Tesla Product Launches",
    description: "All major Tesla product announcements and launch coverage",
    itemCount: 24,
    autoUpdate: true,
    rule: 'company = "Tesla" AND type = "launch"',
    lastUpdated: "2 hours ago",
  },
  {
    id: 2,
    name: "SpaceX Mission Updates",
    description: "Real-time updates on all SpaceX launches and missions",
    itemCount: 18,
    autoUpdate: true,
    rule: 'company = "SpaceX" AND topic INCLUDES "mission"',
    lastUpdated: "1 hour ago",
  },
  {
    id: 3,
    name: "Neuralink Medical Progress",
    description: "Medical breakthroughs and patient stories from Neuralink",
    itemCount: 12,
    autoUpdate: true,
    rule: 'company = "Neuralink" AND category = "medical"',
    lastUpdated: "3 hours ago",
  },
];

const mockBriefs = [
  {
    id: 1,
    title: "Weekly Innovation Summary",
    length: "700 words",
    generatedDate: "Today",
    sources: 42,
    export: "PDF, Notion, Email",
  },
  {
    id: 2,
    title: "SpaceX Starship Week",
    length: "500 words",
    generatedDate: "Yesterday",
    sources: 28,
    export: "PDF, Notion",
  },
];

export default function Library() {
  const [selectedCollection, setSelectedCollection] = useState<number | null>(null);
  const [newCollectionName, setNewCollectionName] = useState("");

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08, delayChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    },
  };

  return (
    <div className="space-y-8">
      <Tabs defaultValue="saved" className="w-full">
        <TabsList className="grid w-full grid-cols-3 border-b border-border bg-transparent">
          <TabsTrigger value="saved">Saved Items</TabsTrigger>
          <TabsTrigger value="collections">Collections</TabsTrigger>
          <TabsTrigger value="briefs">AI Briefs</TabsTrigger>
        </TabsList>

        {/* Saved Items */}
        <TabsContent value="saved" className="space-y-6 mt-8">
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {mockSavedItems.map((item) => (
              <motion.div
                key={item.id}
                variants={itemVariants}
                className="border border-border p-6 hover:border-foreground/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-foreground mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {item.source} • Saved {item.savedDate}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      className="p-2 hover:bg-card transition-colors"
                    >
                      <Share2 className="h-4 w-4 text-muted-foreground" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      className="p-2 hover:bg-card transition-colors"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </motion.button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </TabsContent>

        {/* Collections */}
        <TabsContent value="collections" className="space-y-6 mt-8">
          {/* Create Collection */}
          <motion.div variants={itemVariants} className="border border-border p-6">
            <h3 className="text-lg font-medium mb-4">Create New Collection</h3>
            <div className="space-y-4">
              <Input
                placeholder="Collection name (e.g., 'Tesla Announcements')"
                value={newCollectionName}
                onChange={(e) => setNewCollectionName(e.target.value)}
                className="bg-transparent border-b border-border text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-foreground transition-colors py-3 px-0"
              />
              <div className="flex gap-3">
                <Button variant="outline" className="border-border">
                  Set Auto-Update Rule
                </Button>
                <Button className="ml-auto bg-foreground text-background hover:bg-foreground/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Collection
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Collections List */}
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {mockCollections.map((collection) => (
              <motion.div
                key={collection.id}
                variants={itemVariants}
                onClick={() => setSelectedCollection(selectedCollection === collection.id ? null : collection.id)}
                className={`border p-6 transition-colors cursor-pointer ${
                  selectedCollection === collection.id
                    ? "border-foreground bg-card"
                    : "border-border hover:border-foreground/50"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <Folder className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-medium text-foreground mb-1">
                        {collection.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {collection.description}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-xs text-muted-foreground">
                    <div className="font-semibold text-foreground">
                      {collection.itemCount}
                    </div>
                    <div>items</div>
                  </div>
                </div>

                {selectedCollection === collection.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-4 pt-4 border-t border-border space-y-2 text-xs"
                  >
                    <div>
                      <span className="text-muted-foreground">Rule: </span>
                      <code className="text-foreground">{collection.rule}</code>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Auto-updated: </span>
                      {collection.lastUpdated}
                    </div>
                    <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                      <Button size="sm" variant="outline" className="border-border">
                        Edit Rule
                      </Button>
                      <Button size="sm" className="bg-foreground text-background hover:bg-foreground/90">
                        Share Collection
                      </Button>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </TabsContent>

        {/* AI Briefs */}
        <TabsContent value="briefs" className="space-y-6 mt-8">
          {/* Generate Brief */}
          <motion.div variants={itemVariants} className="border border-border p-6">
            <h3 className="text-lg font-medium mb-4">Generate AI Brief</h3>
            <div className="space-y-4">
              <div className="flex gap-3">
                <Button variant="outline" className="border-border">
                  100 words (TL;DR)
                </Button>
                <Button variant="outline" className="border-border">
                  500 words
                </Button>
                <Button className="bg-foreground text-background hover:bg-foreground/90">
                  700 words (Full)
                </Button>
              </div>
              <Button className="w-full bg-foreground text-background hover:bg-foreground/90">
                Generate Brief
              </Button>
            </div>
          </motion.div>

          {/* Briefs List */}
          <motion.div
            className="space-y-4"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {mockBriefs.map((brief) => (
              <motion.div
                key={brief.id}
                variants={itemVariants}
                className="border border-border p-6 hover:border-foreground/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <h3 className="text-lg font-medium text-foreground">
                        {brief.title}
                      </h3>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {brief.length} • {brief.sources} sources • Generated {brief.generatedDate}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="border-border">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                  <Button size="sm" variant="outline" className="border-border">
                    Read Brief
                  </Button>
                  <Button size="sm" className="ml-auto bg-foreground text-background hover:bg-foreground/90">
                    Share
                  </Button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
